import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ModalFirsError} from 'components/common/ModalFirsError';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {DateRange} from 'components/ui-kit/DateRange';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$} from 'models/auth';
import {dictionary$} from 'models/codeLocks';
import {BlockingAccessors, BlockingGridFormattedRow, GridFieldConfig, UserPermission} from 'types';
import {
    ProductBlockingCodesResponseData,
    ProductBlockingTableDictionary,
} from 'types/products-blocking';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {Controls} from '../DocumentsGrid/Controls';
import {GridsNavigation} from '../GridsNavigation';
import {ControlsWrapper, HeaderControls, NavigationWrapper} from '../MovingGrid/styled';

import {BlocksGridTable} from './Table';
import {TableSearch} from './Table/search';

const mapDictionarySelectOptions = (
    values: string[] | undefined,
    getLabel: (value: string) => string = (value) => value,
) => (values ?? []).map((value) => ({label: getLabel(value), value}));

const getConfig = (t: TFunction): GridFieldConfig[] => {
    const dictionary: ProductBlockingTableDictionary = dictionary$.getState();

    return [
        {
            id: BlockingAccessors.id,
            header: t('pages.documents.blocking.columns.id'),
            isSortable: true,
            width: 340,
        },
        {
            id: BlockingAccessors.createdDate,
            header: t('pages.documents.blocking.columns.createdAt'),
            filter: <DateRange />,
            isSortable: true,
            width: 200,
        },
        {
            id: BlockingAccessors.formationProcess,
            header: t('pages.documents.blocking.columns.formationProcess'),
            filter: (
                <Select
                    isClearable
                    options={mapDictionarySelectOptions(dictionary.formationProcess, (value) =>
                        t(`pages.documents.blocking.formationProcess.${value}`),
                    )}
                />
            ),
            isSortable: false,
            width: 195,
        },
        {
            id: BlockingAccessors.reason,
            header: t('pages.documents.blocking.columns.reason'),
            filter: (
                <Select
                    isClearable
                    options={mapDictionarySelectOptions(dictionary.reason, (value) =>
                        t(`dictionaries.products.blockingReason.${value}`),
                    )}
                />
            ),
            isSortable: true,
            width: 210,
        },
        {
            id: BlockingAccessors.participantTin,
            header: t('pages.documents.blocking.columns.participantTin'),
            isSortable: true,
            width: 170,
        },
        {
            id: BlockingAccessors.organizationName,
            header: t('pages.documents.blocking.columns.organizationName'),
            filter: (
                <Select
                    isClearable
                    options={mapDictionarySelectOptions(dictionary.organizationName)}
                />
            ),
            isSortable: true,
            width: 200,
        },
        {
            id: BlockingAccessors.codesCount,
            header: t('pages.documents.blocking.columns.codesCount'),
            isSortable: true,
            width: 180,
        },
        {
            id: BlockingAccessors.status,
            header: t('pages.documents.blocking.columns.status'),
            filter: (
                <Select
                    isClearable
                    options={mapDictionarySelectOptions(dictionary.status, (value) =>
                        t(`dictionaries.documents.status.${value}`),
                    )}
                />
            ),
            isSortable: false,
            width: 220,
        },
    ];
};

export function convertData(
    data: ProductBlockingCodesResponseData[] | undefined,
    t: TFunction,
): BlockingGridFormattedRow[] {
    return (data ?? []).map((item) => ({
        [BlockingAccessors.id]: handleNil(item.reportId, (reportId: string) => (
            <PermissionAccess
                permissions={[UserPermission.DOCUMENT_DETAIL_VIEW]}
                placeholder={reportId}
            >
                <Link
                    to={{
                        pathname: generatePath(ROUTE.DOCUMENTS_DETAIL, {id: reportId}),
                        state: {from: ROUTE.DOCUMENTS_BLOCKING},
                    }}
                >
                    {reportId}
                </Link>
            </PermissionAccess>
        )),
        [BlockingAccessors.createdDate]: handleNil(item.createdAt, (value: string) =>
            formatDate(new Date(value), 'dd.MM.yyyy, HH:mm'),
        ),
        [BlockingAccessors.formationProcess]: handleNil(item.formationProcess, (value: string) =>
            t(`pages.documents.blocking.formationProcess.${value}`),
        ),
        [BlockingAccessors.reason]: handleNil(item.reason, (value: string) =>
            t(`dictionaries.products.blockingReason.${value}`),
        ),
        [BlockingAccessors.participantTin]: handleNil(item.participantTin),
        [BlockingAccessors.organizationName]: handleNil(item.organizationName),
        [BlockingAccessors.codesCount]: handleNil(item.codesCount),
        [BlockingAccessors.status]: handleNil(item.status, (status: DocumentStatus) => (
            <StatusWrapper color={DOCUMENT_STATUS_COLORS[status] ?? DOCUMENT_STATUS_COLORS.DEFAULT}>
                {t(`dictionaries.documents.status.${status}`)}
            </StatusWrapper>
        )),
    }));
}

export const BlocksGridPage = () => {
    const {t} = useTranslation();
    const isCustomsRole = useStore(isCustomsRole$);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                {!isCustomsRole && <Controls />}
            </ControlsWrapper>
            <NavigationWrapper needMargin>
                {!isCustomsRole && <GridsNavigation />}
            </NavigationWrapper>
        </HeaderControls>
    );

    return (
        <Page title={t('menu.Documents')}>
            <ModalFirsError />
            <BlocksGridTable
                configFunc={getConfig}
                dataMapper={convertData}
                headerControls={headerControls}
                headerControlsAlignItems="flex-end"
            />
        </Page>
    );
};
